import { CallbackEvent } from '@shopify/polaris-types';
import { debounce, DebouncedFunc } from 'lodash';
import { memo, useCallback, useId, useRef } from 'react';
import { useEventListener } from '../../../../hooks';
import { useIsomorphicLayoutEffect } from '../../../../hooks/utils/useIsomorphicLayoutEffect';
import {
    S_INDEX_TABLE_SELECT_ALL_ITEMS,
    SIndexTableRange,
    SIndexTableSelectionType,
    useSIndexTableContext,
    useSIndexTableSelectionChange,
} from '../index-provider';

export const SIndexTableRow = memo(function SIndexTableRow({
    children,
    selected,
    id,
    position,
    disabled,
    selectionRange,
    accessibilityLabel,
    onClick,
}: SIndexTableRowProps) {
    const _componentId = useId();
    const { selectable, selectedItemsCount } = useSIndexTableContext();
    const onSelectionChange = useSIndexTableSelectionChange();
    const ref = useRef<HTMLElementTagNameMap['s-table-row']>(null);
    // The checkbox's `change` event doesn't carry modifier keys, so the shift state is captured
    // from the `click` that precedes it (native checkbox interactions always fire click before
    // change) and read back when the selection is actually applied below.
    const shiftKeyRef = useRef(false);

    useEventListener(
        'click',
        (event) => {
            shiftKeyRef.current = event.shiftKey;
        },
        ref
    );

    const handleSelect = useCallback(() => {
        if (disabled || !selectable || !onSelectionChange) return;

        const selection: string | SIndexTableRange = selectionRange ?? id;
        // Shift+click selects every row between the last selected one and this one (mirrors
        // Shopify's own IndexTable Row.js behavior) — it takes priority over a group selectionRange.
        const selectionType = shiftKeyRef.current
            ? SIndexTableSelectionType.Multi
            : selectionRange
              ? SIndexTableSelectionType.Range
              : SIndexTableSelectionType.Single;

        onSelectionChange(selectionType, !selected, selection, position);
    }, [id, onSelectionChange, selected, selectionRange, position, disabled, selectable]);

    let handleRowSelect: DebouncedFunc<(event: CallbackEvent<'s-checkbox'>) => void>;

    if (!disabled && selectable) {
        handleRowSelect = debounce((event: CallbackEvent<'s-checkbox'>) => {
            event.preventDefault();
            event.stopPropagation();
            handleSelect();
        }, 100);
    }

    let handleRowClick: DebouncedFunc<(event: PointerEvent) => void>;

    if (!disabled && !!onClick) {
        handleRowClick = debounce((event: PointerEvent) => {
            event.preventDefault();
            event.stopPropagation();
            if (!(event?.target as HTMLElement)?.closest?.('[data-prevent-propagation]')) {
                onClick?.();
            }
        }, 100);
    }

    useEventListener('click', handleRowClick ?? (() => {}), ref);

    useIsomorphicLayoutEffect(() => {
        if (!!ref?.current?.shadowRoot && !!onClick) {
            setTimeout(() => {
                const row = ref?.current?.shadowRoot?.querySelector('.table-row');
                row?.setAttribute('style', 'cursor: pointer;');
            }, 0);
        }
    }, [onClick, ref.current]);

    return (
        <s-table-row clickDelegate={selectable ? `s-index-table-row-${_componentId}-checkbox` : undefined} ref={ref}>
            {selectable ? (
                <s-table-cell>
                    <s-checkbox
                        checked={selected === true || selectedItemsCount === S_INDEX_TABLE_SELECT_ALL_ITEMS}
                        onChange={(event) => handleRowSelect?.(event)}
                        accessibilityLabel={accessibilityLabel}
                        id={`s-index-table-row-${_componentId}-checkbox`}
                        disabled={disabled}
                    />
                </s-table-cell>
            ) : null}

            {children}
        </s-table-row>
    );
});

export interface SIndexTableRowProps {
    children: React.ReactNode;
    id: string;
    selected?: boolean | 'indeterminate';
    position: number;
    disabled?: boolean;
    selectionRange?: SIndexTableRange;
    accessibilityLabel?: string;
    onClick?: () => void;
}
